// The comparison page's one set of formulas (Drew, 2026-09-30).
//
// Before this file the page told the same story six times with three different
// formulas: a headline "Est. Future Value Economic Difference" built from tax only,
// a table row with the same title built from total economic cost (which carries
// the IRMAA change), doors that counted future taxes avoided only when the Roth
// lever moved, journey cards that counted all of it, and exports that copied each
// one. On a 65+ client the top of the page and the bottom disagreed by the
// Medicare premium; on a baseline that already carried a Roth conversion every
// strategy was credited with the baseline's own future taxes avoided (bug-hunt
// findings F4 and F5, 2026-09-28).
//
// Everything a net figure needs is derived here, once, from engine output, and
// every surface reads it. Nothing in this file calls the engine.
//
//   netOutOfPocket   = strategy.totalEconomicCost − baseline.totalEconomicCost
//                      (tax + the alt check + a year of Medicare premiums, engine.ts)
//   cashBack         = strategy alt cash back − baseline alt cash back
//   futureTaxAvoided = strategy − baseline, counted only when the strategy moved
//                      the Roth lever (an alt lowers this year's effective rate,
//                      which the engine multiplies the Roth balance by, so an
//                      alt-only strategy would otherwise show a negative Roth line)
//   aheadByHorizon   = cashBack + futureTaxAvoided − netOutOfPocket
//
// Shauna's rule (Tax Goddess, 2026-09-17) is kept by presentation, not by hiding
// the net: the tax the alt saves is its own line, computed against the same
// strategy run with the alt at zero, and capital at risk is printed beside every
// net figure so nobody reads the net as the exposure.

import type { TaxResults, TaxScenario, TaxScenarioInput } from "@/tax/types";
import { formatCurrency } from "@/tax/format";
import { JOURNEY_ROTH_SPREAD_YEARS } from "@/tax/strategyJourney";

// ── Bracket reading ──────────────────────────────────────────────────────────
// One rule for "what bracket is this client in", shared by the strategy card's
// meter and the comparison table. With no ordinary income the engine reports the
// bracket that holds $0 (10%), and printing that to a client owing six figures on
// capital gains is the lie Andrew hit on 2026-09-22. The rule refuses to name a
// bracket when the ordinary slice is zero.

export interface BracketReading {
  /** 'none' = no ordinary income; 'top' = top bracket, no ceiling; 'in' = inside a bracket. */
  kind: "none" | "top" | "in";
  /** "24%". Empty when kind is 'none'. */
  rate: string;
  /** 0–100, how far the ordinary slice is through its bracket. 100 in the top bracket, 0 with none. */
  pct: number;
  /** Dollars of ordinary income left before the next bracket. 0 for top and none. */
  room: number;
  /** "Ordinary income · 24%" / "No ordinary income". */
  left: string;
  /** "$57,356 left in 22%" / "Top bracket" / "All $X at capital-gains rates" / "No taxable income". */
  right: string;
  aria: string;
}

/** Null when the result predates the engine returning bracket bounds. */
export function bracketReading(results: TaxResults): BracketReading | null {
  const { ordinaryTaxableIncome, ordinaryBracketRate, ordinaryBracketFloor, ordinaryBracketCeiling } = results;
  if (ordinaryBracketRate === undefined || ordinaryBracketFloor === undefined || ordinaryBracketCeiling === undefined) {
    return null;
  }
  const rate = `${Math.round(ordinaryBracketRate * 100)}%`;
  if (ordinaryTaxableIncome <= 0) {
    return {
      kind: "none",
      rate: "",
      pct: 0,
      room: 0,
      left: "No ordinary income",
      right: results.taxableIncome > 0 ? `All ${formatCurrency(results.taxableIncome)} at capital-gains rates` : "No taxable income",
      aria: "No ordinary taxable income; all taxable income is taxed at capital-gains rates",
    };
  }
  if (ordinaryBracketCeiling === null) {
    return {
      kind: "top",
      rate,
      pct: 100,
      room: 0,
      left: `Ordinary income · ${rate}`,
      right: "Top bracket",
      aria: `Ordinary taxable income is 100% of the way through the ${rate} bracket`,
    };
  }
  const pct = Math.min(100, Math.max(0, ((ordinaryTaxableIncome - ordinaryBracketFloor) / (ordinaryBracketCeiling - ordinaryBracketFloor)) * 100));
  const room = Math.max(0, ordinaryBracketCeiling - ordinaryTaxableIncome);
  return {
    kind: "in",
    rate,
    pct,
    room,
    left: `Ordinary income · ${rate}`,
    right: `${formatCurrency(room)} left in ${rate}`,
    aria: `Ordinary taxable income is ${Math.round(pct)}% of the way through the ${rate} bracket`,
  };
}

// ── Levers ───────────────────────────────────────────────────────────────────

export interface Levers {
  roth: number;
  ltcg: number;
  alt: number;
  any: boolean;
}

/** The levers a strategy actually moved off the baseline — same detection the Strategy Story uses. */
export function leversOf(s: TaxScenario, baseScenario: TaxScenario): Levers {
  const moved = (key: "rothConversionAmount" | "longTermCapitalGains" | "alternativeInvestmentAmount") =>
    (s.input[key] ?? 0) - (baseScenario.input[key] ?? 0);
  const roth = moved("rothConversionAmount");
  const ltcg = moved("longTermCapitalGains");
  const alt = s.input.alternativeInvestmentAmount > 0 ? s.input.alternativeInvestmentAmount : 0;
  return {
    roth: Math.abs(roth) >= 0.005 ? roth : 0,
    ltcg: Math.abs(ltcg) >= 0.005 ? ltcg : 0,
    alt,
    any: Math.abs(roth) >= 0.005 || Math.abs(ltcg) >= 0.005 || alt > 0,
  };
}

/**
 * "Put $50,000 into the alt" / "Convert $100,000 and put $50,000 into the alt".
 * The old doors' wording, unchanged. The new page says what a conversion and a
 * gain are ("to Roth", "of long-term gains" — Andrew, 2026-09-23: always say
 * long-term capital gains) through `long`.
 */
export function moveTitle(levers: Levers, long = false): string {
  const parts: string[] = [];
  if (levers.roth) parts.push(`convert ${formatCurrency(Math.abs(levers.roth))}${long ? " to Roth" : ""}`);
  if (levers.ltcg) parts.push(`realize ${formatCurrency(Math.abs(levers.ltcg))} ${long ? "of long-term capital gains" : "in gains"}`);
  if (levers.alt) parts.push(`put ${formatCurrency(levers.alt)} into the alt`);
  const joined =
    parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

/** True when the strategy is the baseline plus an alt and nothing else — its without-alt run IS the baseline. */
export function isBaselinePlusAlt(s: TaxScenario, baseScenario: TaxScenario): boolean {
  const strip = (i: TaxScenarioInput) =>
    JSON.stringify({ ...i, name: "", alternativeInvestmentAmount: 0, uiState: undefined, altFundVintageId: undefined });
  return strip(s.input) === strip(baseScenario.input);
}

/** Strategies whose tax split needs a second engine run: an alt plus another lever. */
export function needsWithoutAltRun(s: TaxScenario, baseScenario: TaxScenario): boolean {
  return s.id !== baseScenario.id && s.input.alternativeInvestmentAmount > 0 && !isBaselinePlusAlt(s, baseScenario);
}

// ── The plan ─────────────────────────────────────────────────────────────────

export interface PlanRow {
  label: string;
  value: number;
  /** How the number reads to the client. 'bad' = money out or tax up, 'good' = money back or tax down. */
  tone?: "good" | "bad";
  /** Indented under the row above ("→ Tax on the conversion"). */
  arrow?: boolean;
}

export interface JourneyPoint {
  year: number;
  position: number;
  altCashBack: number;
  rothTaxAvoided: number;
}

export interface StrategyPlan {
  id: string;
  levers: Levers;
  title: string;
  /** This year's money in and out, in the order the client reads it. Sums to netOutOfPocket to the dollar. */
  thisYear: PlanRow[];
  /** True when the tax split is a single combined line because the without-alt run is not in yet. */
  taxSplitPending: boolean;
  /** Change in total economic cost vs the baseline. Negative = the client is ahead this year. */
  netOutOfPocket: number;
  /** 12 × the change in monthly Medicare premiums. 0 unless an IRMAA tier moved. */
  medicareDelta: number;
  /** Tax saved by the alt's deduction alone (0 without an alt). */
  deductionSaved: number;
  /** Tax the other levers cost before the alt's deduction (0 without them). */
  leverTax: number;
  totalTax: number;
  /** Baseline total tax minus strategy total tax. Positive = saves tax this year. */
  taxVsBaseline: number;
  /** Rows for the horizon column, after the "net out of pocket this year" opener. */
  later: PlanRow[];
  cashBack: number;
  rothFutureValue: number;
  futureTaxAvoided: number;
  aheadByHorizon: number;
  horizonYears: number;
  journey: JourneyPoint[];
  /** Fractional year the line first crosses back above zero after going negative; null if never negative or never back. */
  breakevenYear: number | null;
  /** The investment on its own. Null without an alt. */
  alt: {
    capital: number;
    years: number;
    cashBack: number;
    moic: number;
    irr: number;
    biggestYear: number;
    biggestAmount: number;
    /** Years 1..N, scaled to this strategy's check. */
    flows: number[];
    label: string;
  } | null;
}

const r = (v: number) => Math.round(v || 0);

/**
 * Build one strategy's plan against the baseline.
 *
 * `withoutAlt` is the same strategy scored with the alt at zero — the engine's
 * answer to "what did the alt's deduction save on its own". For a strategy that is
 * the baseline plus an alt the baseline IS that run and the caller passes it;
 * for a Roth-or-gains-plus-alt strategy the caller runs it; while it is pending
 * or failed, pass null and the tax shows as one combined line.
 */
export function buildStrategyPlan(
  strategy: TaxScenario,
  baseline: TaxScenario,
  withoutAlt: TaxResults | null,
  horizonYears: number,
): StrategyPlan {
  const s = strategy.results;
  const b = baseline.results;
  const levers = leversOf(strategy, baseline);
  const altCapital = Math.max(0, (s.alternativeInvestmentCapital || 0) - (b.alternativeInvestmentCapital || 0));
  const netOutOfPocket = r(s.totalEconomicCost - b.totalEconomicCost);
  const medicareDelta = r(((s.totalMedicarePremium || 0) - (b.totalMedicarePremium || 0)) * 12);
  const taxVsBaseline = r(b.totalTaxLiability - s.totalTaxLiability);

  // This year's column. Each lever carries its own tax; the deduction's saving is the
  // balancing figure on whole dollars so the rounded rows always sum to the total.
  const thisYear: PlanRow[] = [];
  let leverTax = 0;
  let deductionSaved = 0;
  let taxSplitPending = false;
  if (levers.roth) thisYear.push({ label: "Roth conversion", value: levers.roth });
  if (levers.ltcg) thisYear.push({ label: "Long-term capital gains realized", value: levers.ltcg });
  const otherLevers = Boolean(levers.roth || levers.ltcg);
  if (altCapital > 0) {
    const split = withoutAlt ?? (otherLevers ? null : b);
    if (split) {
      leverTax = otherLevers ? r(split.totalTaxLiability - b.totalTaxLiability) : 0;
      deductionSaved = leverTax + r(altCapital) + medicareDelta - netOutOfPocket;
      if (otherLevers) thisYear.push({ label: leverTaxLabel(levers), value: leverTax, tone: leverTax >= 0 ? "bad" : "good", arrow: true });
      thisYear.push({ label: "Alt investment", value: r(altCapital), tone: "bad" });
      thisYear.push({ label: "Alt deduction", value: r(s.hpeImpliedDeduction || 0), arrow: true });
      thisYear.push({ label: "Tax saved by the deduction", value: deductionSaved, tone: deductionSaved >= 0 ? "good" : "bad", arrow: true });
    } else {
      taxSplitPending = true;
      const combined = r(s.totalTaxLiability - b.totalTaxLiability);
      thisYear.push({ label: "Alt investment", value: r(altCapital), tone: "bad" });
      thisYear.push({ label: "Alt deduction", value: r(s.hpeImpliedDeduction || 0), arrow: true });
      thisYear.push({ label: combined >= 0 ? "Tax increase this year" : "Tax decrease this year", value: combined, tone: combined >= 0 ? "bad" : "good", arrow: true });
      leverTax = combined; // one combined line: everything but the alt check and Medicare
    }
  } else if (otherLevers) {
    // The tax line is the balancing figure, as the alt's deduction saving is above:
    // net out of pocket and the Medicare line are each rounded on their own, and
    // on a retiree with a pension they rounded apart by a dollar (found 2026-10-08
    // when the F5 test's pension key was corrected), so the column did not foot.
    leverTax = netOutOfPocket - medicareDelta;
    thisYear.push({ label: leverTaxLabel(levers), value: leverTax, tone: leverTax >= 0 ? "bad" : "good", arrow: true });
  } else {
    // No lever the page names moved (e.g. an income edit). One honest line.
    leverTax = netOutOfPocket - medicareDelta;
    thisYear.push({ label: leverTax >= 0 ? "Tax increase this year" : "Tax decrease this year", value: leverTax, tone: leverTax >= 0 ? "bad" : "good" });
  }
  if (medicareDelta !== 0) {
    thisYear.push({ label: medicareDelta > 0 ? "Medicare (IRMAA) premium increase" : "Medicare (IRMAA) premium decrease", value: medicareDelta, tone: medicareDelta > 0 ? "bad" : "good" });
  }

  // The horizon column.
  const cashBack = r((s.hpeNetCashReturned || 0) - (b.hpeNetCashReturned || 0));
  const rothFutureValue = levers.roth ? r(s.rothFutureValue || 0) : 0;
  const futureTaxAvoided = levers.roth ? r((s.estimatedFutureTaxAvoided || 0) - (b.estimatedFutureTaxAvoided || 0)) : 0;
  const aheadByHorizon = cashBack + futureTaxAvoided - netOutOfPocket;
  const later: PlanRow[] = [];
  if (cashBack) later.push({ label: "Cash back from the alt", value: cashBack, tone: cashBack >= 0 ? "good" : "bad", arrow: true });
  if (levers.roth) {
    later.push({ label: "Roth balance, tax-free", value: rothFutureValue, arrow: true });
    later.push({ label: "Future taxes avoided", value: futureTaxAvoided, tone: futureTaxAvoided >= 0 ? "good" : "bad", arrow: true });
  }

  // Year by year. Year 0 is signing day: the alt check goes out before any tax comes
  // back (Shauna's point, 2026-09-17). From Year 1 the position is cash back so far,
  // plus the Roth's future taxes avoided credited in equal slices, less net out of pocket.
  const sFlows = s.hpeCashFlows ?? [];
  const bFlows = b.hpeCashFlows ?? [];
  const journey: JourneyPoint[] = [];
  let altSoFar = 0;
  for (let t = 0; t <= horizonYears; t++) {
    if (t >= 1) altSoFar += (sFlows[t] || 0) - (bFlows[t] || 0);
    const rothSoFar = futureTaxAvoided * (Math.min(t, JOURNEY_ROTH_SPREAD_YEARS) / JOURNEY_ROTH_SPREAD_YEARS);
    journey.push({
      year: t,
      position: t === 0 ? -r(altCapital) : r(altSoFar + rothSoFar - netOutOfPocket),
      altCashBack: r(altSoFar),
      rothTaxAvoided: r(rothSoFar),
    });
  }

  const alt = altCapital > 0
    ? (() => {
        const flows = sFlows.slice(1).map(r);
        let biggestYear = 0;
        let biggestAmount = 0;
        flows.forEach((v, i) => { if (v > biggestAmount) { biggestAmount = v; biggestYear = i + 1; } });
        return {
          capital: r(altCapital),
          years: Math.max(0, sFlows.length - 1),
          cashBack: r(s.hpeNetCashReturned || 0),
          moic: s.hpeMoic || 0,
          irr: s.hpeIRR || 0,
          biggestYear,
          biggestAmount,
          flows,
          label: s.hpeFundLabel || "",
        };
      })()
    : null;

  return {
    id: strategy.id,
    levers,
    title: levers.any ? moveTitle(levers, true) : "Adjust the inputs",
    thisYear,
    taxSplitPending,
    netOutOfPocket,
    medicareDelta,
    deductionSaved,
    leverTax,
    totalTax: r(s.totalTaxLiability),
    taxVsBaseline,
    later,
    cashBack,
    rothFutureValue,
    futureTaxAvoided,
    aheadByHorizon,
    horizonYears,
    journey,
    breakevenYear: breakevenOf(journey),
    alt,
  };
}

function leverTaxLabel(levers: Levers): string {
  if (levers.roth && levers.ltcg) return "Tax on the conversion and the gains";
  if (levers.roth) return "Tax on the conversion";
  return "Tax on the gains";
}

/** First upward crossing of zero after the line has been below it, on the straight segments the chart draws. */
export function breakevenOf(points: JourneyPoint[]): number | null {
  let wasNegative = false;
  for (let i = 0; i < points.length; i++) {
    const p = points[i].position;
    if (p < 0) wasNegative = true;
    if (wasNegative && p >= 0 && i > 0) {
      const a = points[i - 1].position;
      return i - 1 + a / (a - p);
    }
  }
  return null;
}

/** "whole again in Year 3" / "ahead from day one" / "not whole by Year 8". */
export function breakevenText(plan: StrategyPlan): string {
  const everNegative = plan.journey.some((p) => p.position < 0);
  if (!everNegative) return "ahead from day one";
  if (plan.breakevenYear === null) return `not whole by Year ${plan.horizonYears}`;
  return `whole again in Year ${Math.max(1, Math.ceil(plan.breakevenYear))}`;
}

/** Sum of the this-year rows the way the column prints them. Equals netOutOfPocket to the dollar. */
export function thisYearSum(plan: StrategyPlan): number {
  let sum = 0;
  for (const row of plan.thisYear) {
    if (row.label === "Roth conversion" || row.label === "Long-term capital gains realized" || row.label === "Alt deduction") continue;
    if (row.label === "Tax saved by the deduction") sum -= row.value;
    else sum += row.value;
  }
  return sum;
}

// ── The answer strip's sentence ────────────────────────────────────────────

const fmt$ = (v: number) => formatCurrency(Math.abs(v));

/**
 * The sentence a client would repeat to their spouse, built from the plan's own
 * rows. Every number in it is one the sections below explain.
 */
export function storySentence(plan: StrategyPlan, baseline: TaxScenario, horizon: number): string {
  const lv = plan.levers;
  const tax = baseline.results.totalTaxLiability;
  if (plan.alt && !lv.roth && !lv.ltcg) {
    return `Put ${fmt$(plan.alt.capital)} into the alt. The deduction cuts this year's tax by ${fmt$(plan.deductionSaved)}, so tax plus the check comes to ${fmt$(tax - plan.deductionSaved + plan.alt.capital)}, ${fmt$(plan.netOutOfPocket)} ${plan.netOutOfPocket < 0 ? "less" : "more"} than the ${fmt$(tax)} of tax alone. The fund's schedule returns ${fmt$(plan.cashBack)} over ${horizon} years.`;
  }
  if (lv.roth && !plan.alt && !lv.ltcg) {
    const medicare = plan.medicareDelta ? ` and ${fmt$(plan.medicareDelta)} ${plan.medicareDelta > 0 ? "more" : "less"} in Medicare premiums` : "";
    return `Convert ${fmt$(lv.roth)} to Roth. It costs ${fmt$(plan.leverTax)} in tax this year${medicare}, paid from outside funds. By Year ${horizon} the Roth holds ${fmt$(plan.rothFutureValue)} tax-free, worth ${fmt$(plan.futureTaxAvoided)} in taxes it will never owe.`;
  }
  if (lv.roth && plan.alt) {
    const andGains = lv.ltcg ? ` and ${fmt$(lv.ltcg)} of long-term capital gains` : "";
    return `Convert ${fmt$(lv.roth)} to Roth${andGains} and put ${fmt$(plan.alt.capital)} into the alt. The deduction's ${fmt$(plan.deductionSaved)} of tax saved ${plan.deductionSaved >= plan.leverTax ? "covers" : "offsets"} the ${fmt$(plan.leverTax)} the ${lv.ltcg ? "conversion and the gains cost" : "conversion costs"}, leaving ${fmt$(plan.netOutOfPocket)} ${plan.netOutOfPocket < 0 ? "ahead" : "out of pocket"} this year. By Year ${horizon}: ${fmt$(plan.cashBack)} back from the fund and a ${fmt$(plan.rothFutureValue)} tax-free Roth.`;
  }
  if (lv.ltcg && plan.alt) {
    return `Realize ${fmt$(lv.ltcg)} of long-term capital gains and put ${fmt$(plan.alt.capital)} into the alt. The deduction's ${fmt$(plan.deductionSaved)} of tax saved ${plan.deductionSaved >= plan.leverTax ? "covers" : "offsets"} the ${fmt$(plan.leverTax)} the gains cost, leaving ${fmt$(plan.netOutOfPocket)} ${plan.netOutOfPocket < 0 ? "ahead" : "out of pocket"} this year. The fund's schedule returns ${fmt$(plan.cashBack)} over ${horizon} years.`;
  }
  return `${plan.title}. Net ${plan.netOutOfPocket < 0 ? "ahead" : "out of pocket"} ${fmt$(plan.netOutOfPocket)} this year; ${fmt$(plan.aheadByHorizon)} ${plan.aheadByHorizon >= 0 ? "ahead" : "behind"} by Year ${horizon}.`;
}

export type YearStep = { x: number; label: string; position: number; altCashBack: number; rothTaxAvoided: number };

/**
 * The points a strategy's chart draws, against doing nothing (Drew, 2026-10-22: "show
 * what they're losing right now … the investment today, tax time, and then letting it go").
 * Today: the alt check alone. Tax time: the whole of this year's net out of pocket — the
 * check, the tax change with the alt's deduction inside it, and a year of Medicare.
 * Then Years 1–N from the journey. Each point gets its own slot on the axis (x is the
 * slot, 0..N+1), so "Today" and "Tax time" never print on top of each other.
 */
export function yearSteps(plan: StrategyPlan): YearStep[] {
  const [today, ...years] = plan.journey;
  return [
    { x: 0, label: "Today", position: today.position || 0, altCashBack: 0, rothTaxAvoided: 0 },
    { x: 1, label: "Tax time", position: -plan.netOutOfPocket || 0, altCashBack: 0, rothTaxAvoided: 0 },
    ...years.map((pt) => ({ x: pt.year + 1, label: `Yr ${pt.year}`, position: pt.position, altCashBack: pt.altCashBack, rothTaxAvoided: pt.rothTaxAvoided })),
  ];
}

/**
 * The "Over N years" chart's Y axis on round steps ($0 / $500K / $1M), never the
 * odd values the chart library picks on its own ($571K / $121K), and always with
 * a labelled $0, the doing-nothing line every plan is measured against. Pass the
 * range the chart must show; it is widened outward to the nearest step, so no
 * point is ever clipped and there are at most seven gaps.
 */
export function roundAxis(lo: number, hi: number): { min: number; max: number; ticks: number[] } {
  const low = Math.min(0, lo);
  const high = Math.max(0, hi);
  const steps = [10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 2_000_000, 5_000_000, 10_000_000];
  const step = steps.find((st) => Math.ceil(high / st) - Math.floor(low / st) <= 7) ?? 10_000_000;
  const min = Math.floor(low / step) * step;
  const max = Math.max(Math.ceil(high / step) * step, min + step);
  const ticks: number[] = [];
  for (let v = min; v <= max; v += step) ticks.push(v);
  return { min, max, ticks };
}

// ── Comparison page v4 (Drew, 2026-11-03) ────────────────────────────────────
// The handoff design: one banner that says why the winning plan wins, one chart of
// every plan's running total against Baseline, a breakdown card, the return table
// and a ledger whose columns add top to bottom. Two timing changes against v3's
// figures, both from the design: Medicare is charged in the year it is paid (two
// years after the return that sets it), and the Roth's future taxes avoided land
// at the end of the period. The end point is the same ahead-by-the-horizon figure.

export interface PlanLedger {
  /** The tax the strategy's other moves create on their own. Null when it has none, or while the split is pending. */
  strategyTax: number | null;
  /** The tax the alt's deduction takes off (negative). Null without an alt, or while the split is pending. */
  scheduleAOffset: number | null;
  /** Total tax change against Baseline. strategyTax + scheduleAOffset = taxChange. */
  taxChange: number;
  altCheck: number;
  altDeduction: number;
  /** taxChange + altCheck. Medicare is not in it: it is paid later. */
  outThisYear: number;
  fundCash: number;
  futureTaxAvoided: number;
  /** The Medicare premium change, paid in the second year after the return. */
  medicare: number;
  /** fundCash + futureTaxAvoided − outThisYear − medicare. Equals plan.aheadByHorizon. */
  aheadBy: number;
  pending: boolean;
}

export function planLedger(plan: StrategyPlan): PlanLedger {
  const altCheck = plan.alt?.capital ?? 0;
  const medicare = plan.medicareDelta;
  const taxChange = plan.netOutOfPocket - medicare - altCheck;
  const otherMoves = Boolean(plan.levers.roth || plan.levers.ltcg);
  const altDeduction = plan.thisYear.find((row) => row.label === "Alt deduction")?.value ?? 0;
  let strategyTax: number | null = taxChange;
  let scheduleAOffset: number | null = null;
  if (altCheck > 0) {
    if (plan.taxSplitPending) {
      strategyTax = null;
    } else if (otherMoves) {
      strategyTax = plan.leverTax;
      scheduleAOffset = taxChange - plan.leverTax;
    } else {
      strategyTax = null;
      scheduleAOffset = taxChange;
    }
  }
  return {
    strategyTax,
    scheduleAOffset,
    taxChange,
    altCheck,
    altDeduction,
    outThisYear: taxChange + altCheck,
    fundCash: plan.cashBack,
    futureTaxAvoided: plan.futureTaxAvoided,
    medicare,
    aheadBy: plan.cashBack + plan.futureTaxAvoided - plan.netOutOfPocket,
    pending: plan.taxSplitPending,
  };
}

/** The chart's title, on the v4 page and in its PDF (Drew, 2026-11-04: the old page's own name for it). */
export const costBenefitTitle = (years: number) => `Cost Benefit Analysis — ${years} Year Horizon`;

export interface RunningTotals {
  /** "Today", "Tax day", then the calendar years after the return. */
  labels: string[];
  /** Doing nothing: $0 today, then the year's tax from Tax day on. */
  baseline: number[];
  /** One line per strategy, the same length as labels. */
  lines: number[][];
  /** Index of the point where the Medicare change is paid. */
  medicareIndex: number;
}

/**
 * Every plan's running total of the client's money against doing nothing, in
 * absolute dollars. Today: the alt check goes out. Tax day: the check plus the
 * year's whole tax. Each year after: plus that year's fund cash, less the Medicare
 * change in the year it is paid, plus the Roth's future taxes avoided at the end.
 * Each line's last point less Baseline's is that plan's ahead-by figure, to the dollar.
 */
export function runningTotals(plans: StrategyPlan[], baseline: TaxScenario): RunningTotals {
  const H = plans[0]?.horizonYears ?? 8;
  const taxYear = baseline.input.taxYear;
  const baseTax = r(baseline.results.totalTaxLiability);
  const labels = ["Today", "Tax day", ...Array.from({ length: H }, (_, k) => String(taxYear + k + 1))];
  const medicareIndex = Math.min(labels.length - 1, 3); // the second year after the return
  const base = labels.map((_, i) => (i === 0 ? 0 : -baseTax));
  const lines = plans.map((plan) => {
    const L = planLedger(plan);
    const start = -L.altCheck;
    const taxDay = start - baseTax - L.taxChange;
    return labels.map((_, i) => {
      if (i === 0) return start;
      if (i === 1) return taxDay;
      const t = i - 1;
      const cash = t >= H ? L.fundCash : plan.journey[t]?.altCashBack ?? 0;
      return taxDay + cash - (i >= medicareIndex ? L.medicare : 0) + (t >= H ? L.futureTaxAvoided : 0);
    });
  });
  return { labels, baseline: base, lines, medicareIndex };
}

/** "2029", "Ahead from day one" or "Not by 2034": the first point the line is back level with Baseline. */
export function wholeAgain(line: number[], base: number[], labels: string[]): { kind: "year" | "never-behind" | "not-yet"; label: string } {
  let behind = false;
  for (let i = 0; i < line.length; i++) {
    const gap = line[i] - base[i];
    if (gap < 0) behind = true;
    else if (behind) return { kind: "year", label: labels[i] };
  }
  return behind ? { kind: "not-yet", label: labels[labels.length - 1] } : { kind: "never-behind", label: labels[0] };
}

const joinAnd = (parts: string[]) =>
  parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;

/**
 * Why the winning plan is the best move, for the banner (Drew, 2026-11-02): what it
 * does, how the deduction offsets the tax, what it returns and where that leaves
 * them. Every number in it is one the breakdown and the ledger show.
 */
export function winnerSentence(plan: StrategyPlan, name: string, endYear: string): string {
  const L = planLedger(plan);
  const lv = plan.levers;
  const moves: string[] = [];
  if (lv.ltcg > 0) moves.push(`harvests ${fmt$(lv.ltcg)} of long-term capital gains`);
  if (lv.ltcg < 0) moves.push(`realizes ${fmt$(lv.ltcg)} less in long-term capital gains`);
  if (lv.roth > 0) moves.push(`converts ${fmt$(lv.roth)} to Roth`);
  if (lv.roth < 0) moves.push(`converts ${fmt$(lv.roth)} less to Roth`);
  if (L.altCheck > 0) moves.push(`puts ${fmt$(L.altCheck)} into the alt`);
  const does = moves.length ? `${name} ${joinAnd(moves)}.` : `${name} changes the inputs.`;

  const change = (only: boolean) =>
    L.taxChange > 0 ? `taxes increase ${only ? "only " : ""}${fmt$(L.taxChange)} this year`
      : L.taxChange < 0 ? `taxes go down ${fmt$(L.taxChange)} this year`
        : "taxes stay the same this year";
  let tax: string;
  if (L.altCheck > 0 && L.strategyTax !== null && L.scheduleAOffset !== null && L.scheduleAOffset < 0 && L.strategyTax > 0) {
    tax = `The alt's ${fmt$(L.altDeduction)} deduction offsets ${fmt$(L.scheduleAOffset)} of the ${fmt$(L.strategyTax)} in tax those moves create, so ${change(L.taxChange > 0)}.`;
  } else if (L.altCheck > 0 && L.strategyTax === null && L.scheduleAOffset !== null && L.scheduleAOffset < 0) {
    tax = `The alt's ${fmt$(L.altDeduction)} deduction cuts taxes by ${fmt$(L.scheduleAOffset)} this year.`;
  } else if (L.altCheck > 0) {
    tax = `With the alt's ${fmt$(L.altDeduction)} deduction, ${change(false)}.`;
  } else {
    const c = change(false);
    tax = `${c.charAt(0).toUpperCase()}${c.slice(1)}.`;
  }

  const back: string[] = [];
  if (L.fundCash > 0) back.push(`the fund returns ${fmt$(L.fundCash)}`);
  if (L.futureTaxAvoided > 0) back.push(`the Roth avoids ${fmt$(L.futureTaxAvoided)} in future taxes`);
  const where = L.aheadBy >= 0 ? `${fmt$(L.aheadBy)} ahead` : `${fmt$(L.aheadBy)} behind`;
  const ret = back.length
    ? `By ${endYear} ${joinAnd(back)}, ${L.aheadBy >= 0 ? "putting" : "leaving"} them ${where}.`
    : `By ${endYear} that leaves them ${where}.`;
  return `${does} ${tax} ${ret}`;
}
